import { Upload, type FormInstance, Modal, notification } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { post } from '@/common/services/httpClient';

import type { AxiosProgressEvent } from 'axios';
import type { FormField } from './DynamicFormTypes';
import { useEffect, useState } from 'react';
import { t } from 'i18next';

interface FileUploadFieldProps {
  field: FormField;
  commonProps: object;
  value?: UploadFile[] | string;
  onChange?: (value: UploadFile[]) => void;
  form: FormInstance;
}
interface UploadApiResponse<T = { path: string }> {
  data: T | null;
  paging?: unknown;
  statusCode: number;
  isError: boolean;
  title?: string;
  developerMessage?: string;
  message?: string;
  messageArgs?: unknown;
  userType?: string | null;
}
const FileUploadField = ({ field, commonProps, value, onChange, form }: FileUploadFieldProps) => {
  const { Dragger } = Upload;
const [fileList, setFileList] = useState<UploadFile[]>([]);
  const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

  useEffect(() => {
    //console.log("value",value);
    if (value) {
      if (typeof value === 'string') {
        const newFileList = [
          {
            uid: '-1',
            name: value,
            status: 'done',
            url: `${VITE_API_BASE_URL.replace(/api$/, '')}${value}`,
          },
        ];
        setFileList(newFileList as UploadFile[]);
        //console.log("newFileList",newFileList);
      } else {
        setFileList(value as UploadFile[]);
      }
    } else {
      setFileList([]);
    }
  }, [value, VITE_API_BASE_URL]);
  const handleFileChange: UploadProps['onChange'] = (info) => {
    let newFileList = [...info.fileList];

    // 1. Limit the number of uploaded files
    if (field.maxCount) {
      newFileList = newFileList.slice(-field.maxCount);
    }

    // 2. Read from response and show file link
    newFileList = newFileList.map((file) => {
      if (file.response) {
        // Component will show file.url as link
        const responseValue = field.responseValueKey ? file.response[field.responseValueKey] : file.response.url;
        file.url = responseValue;
        file.name = responseValue; // Also update the name for display
      }
      return file;
    });

    onChange?.(newFileList);
     setFileList(newFileList);
    form.setFieldsValue({ [field.name]: newFileList });
  };

  const handleRemove = () => {
    Modal.confirm({
      title: 'Confirm Delete',
      content: `Are you sure you want to delete?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      onOk: () => {
        onChange?.([]);
        setFileList([]); // Clear the file list in the component's state
        if (field.updateFieldName) {
          form.setFieldsValue({ [field.updateFieldName]: null }); // Set the target field to null
        }
        return Promise.resolve(true);
      },
      onCancel: () => {
        return Promise.resolve(false);
      },
    });
    return false; // Return false to prevent immediate removal by Ant Design's Upload component
  };

  const customRequest: UploadProps['customRequest'] = async (options) => {
    const { onSuccess, onError, file, onProgress } = options;
    const formData = new FormData();
    const fileKey = field.fileKey || 'file';
    formData.append(fileKey, file as Blob);

    try {
      const response = await post<UploadApiResponse>(field.uploadUrl!, formData, {
        onUploadProgress: (event: AxiosProgressEvent) => {
          if (event.total) {
            const percent = Math.floor((event.loaded / event.total) * 100);
            onProgress?.({ percent });
          }
        },
      });
     if (response.isError) {
      notification.error({
        message: response.title || "Upload Failed",
        description: response.message || "An unknown error occurred while uploading.",
      });
      onError?.(new Error(response.message || "Upload failed"));
      return;
    }
      if (field.updateFieldName && response.data?.path) {
        form.setFieldsValue({ [field.updateFieldName]: response.data.path });
      }
      onSuccess?.(response.data);

     
    } catch (error) {
      onError?.(error as Error);
    }
  };

  const beforeUpload: UploadProps['beforeUpload'] = (file) => {
    
    // Example: Check file size (e.g., max 50MB)
    const isLt2M = file.size / 1024 / 1024 < 50;
    if (!isLt2M) {
      // message.error('File must be smaller than 50MB!');
      console.error('File must be smaller than 50MB!');
      notification.error({
          message: 'Upload Error',
          description: 'File must be smaller than 50MB!',
         });
         return Upload.LIST_IGNORE; // Prevent upload
    }
    return isLt2M;
  };

  const props: UploadProps = {
    ...commonProps,
    name: field.name,
    multiple: (field.maxCount ?? 1) > 1,
    accept: field.accept,
    fileList: fileList as UploadFile[],
    maxCount: field.maxCount,
    customRequest: customRequest,
    onChange: handleFileChange,
    onRemove: handleRemove,
    beforeUpload: beforeUpload,
    listType: "picture-card",
    showUploadList: {
      showRemoveIcon: true, // Show the delete icon
      showPreviewIcon: true, // Show the view button
      showDownloadIcon: false, // Hide the download icon if not needed
    },
  };

  return (
      <Dragger {...props}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
          <p className="ant-upload-text">{t("Click or drag file to this area to upload")}</p>
          <p className="ant-upload-hint">
              {t("form.upload.supportText")}
        </p>
      </Dragger>
  );
};

export default FileUploadField;